export const ADVANCED_FHIR_DOCUMENTATION_MARKDOWN = `
# FHIR v2

Makes a request to the FHIR API instance associated with this practice, allowing more control over the request made than the FHIR block.  
This block inherits many of the features of the [REST API Call block](https://docs.notablehealth.com/platform-components/block-library/rest-api)—be sure to review that doc for basic usage.

---

## Comparison: REST vs. FHIR vs. FHIR v2

| Feature             | REST Block                                        | FHIR Block                                      | FHIR v2 Block                                               |
|---------------------|---------------------------------------------------|-------------------------------------------------|-------------------------------------------------------------|
| **Endpoint scope**  | Any HTTP endpoint                                 | Pre-defined FHIR operations                     | Any FHIR path on your selected API Instance                 |
| **Authentication**  | Manual (error-prone & insecure)                   | Automatic via Notable FHIR service              | Automatic via selected API Instance (secure token rotation) |
| **Rate limiting**   | Not supported                                     | **Configurable** per API Instance               | **Configurable** per API Instance                          |
| **Request body**    | None / JSON / Form URL Encoded                    | N/A (operation inputs only)                     | None / JSON / Form URL Encoded                              |
| **Setup**           | No setup—just URL & auth per block                | Requires upstream GetPatient + FHIR service     | Requires a pre-configured API Instance                      |
| **Flexibility**     | Highest—any API, any shape                        | Lowest—only built-in FHIR operations            | Medium—arbitrary FHIR calls, zero auth boilerplate          |

---

## 🔑 API Instance

- **API Instance**  
  Select the instance (manage API instances [here](https://app.notablehealth.com/staff/app#/api-instances)). API instances are limited to the flow's organization
- **Base URL**  
  Automatically populated from the selected instance (read-only).  
- **Authentication**  
  The block will automatically:
  1. Generate or refresh the correct token (Bearer, Basic, OAuth, etc.)  
  2. Attach it to the request for you
  3. Rotate credentials as needed—no extra blocks required.

---

## 🕑 Rate Limiting & Retries

- Respects the API instance's native rate limits  
- Automatically retries on **429**, **408**, or other transient errors with exponential backoff (up to 3 attempts)  
- Surfaces retry metadata in the block's logs if you need to debug throttling

---

## ⚙️ Configuration Options

| Field                  | Description                                                                                       |
|------------------------|---------------------------------------------------------------------------------------------------|
| **API Instance**       | Pick one of your configured API Instances                                                         |
| **Endpoint**           | _(Epic/Oracle API instances only)_ Select a pre-configured endpoint to automatically populate the method and path. For other API instances, this feature will be coming soon. |
| **Method**             | HTTP verb (GET, POST, PATCH, PUT, DELETE)                                                         |
| **Path**               | Resource path (e.g. \`/Patient\`, \`/Appointment\`)                                               |
| **Parameters**         | Standard query parameters                                                                         |
| **Headers**            | Any additional headers; authentication is handled automatically                                   |
| **Response Body Format** | \`AutoDetect\` (default) parses JSON when possible. \`ParsedBase64\` returns binary payloads (e.g. Epic \`Binary\` PDFs) as a base64-encoded string for downstream Transform File / LLM processing |
| **Body**               | JSON payload for create/update operations                                                         |

### 📋 Endpoint Selection (Epic/Oracle API Instances Only)

For **Epic/Oracle API instances**, you can select a pre-configured endpoint from a dropdown menu instead of manually entering the method and path. When you select an endpoint:
- The **Method** and **Path** fields are automatically populated
- You can still enter a custom method and path if needed after clearing the selected endpoint

**Note:** This feature is currently available only for Epic/Oracle API instances. Support for other API instance types will be coming soon. For other API instances, you'll need to manually select the method and enter the path.

---

_For everything else (secure headers, error handling, metadata bindings, response parsing modes, large-payload caveats, etc.), see the REST block documentation._
`;
